/**
 * 用户管理页面
 * 使用 tenantUser tRPC，管理当前 active tenant 下用户
 */

import { useCallback, useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Popconfirm,
  App,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  Badge,
  Switch,
  Alert,
} from "antd";
import {
  BookOutlined,
  KeyOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyOutlined,
  TeamOutlined,
  UnlockOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from "../components/icons";
import { trpc } from "../trpc";
import { useTenantContext } from "../tenant/TenantContext";
import { getTrpcErrorMessage, tenantUserApi } from "../tenant/api";
import type { TenantUserRecord } from "../tenant/types";
import type { ColumnsType } from "antd/es/table";

const { Title, Text } = Typography;

function roleTag(role: string) {
  if (role === "tenant_admin") return <Tag color="gold">租户管理员</Tag>;
  if (role === "member") return <Tag color="blue">成员</Tag>;
  return <Tag>{role || "未知"}</Tag>;
}

export function UserManagement() {
  const { message } = App.useApp();
  const { context, activeTenantLabel, isPlatformSuperAdmin } = useTenantContext();
  const dispatchAccessEnabled = Boolean(context?.features.dispatch_monitor);
  const scriptAssistantAccessEnabled = Boolean(context?.features.atmosphere_assistant);
  const [users, setUsers] = useState<TenantUserRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TenantUserRecord | null>(null);
  const [allBusinessUnits, setAllBusinessUnits] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessCountMap, setAccessCountMap] = useState<Record<string, number>>({});
  const [scriptAccessModalOpen, setScriptAccessModalOpen] = useState(false);
  const [scriptAccessLoading, setScriptAccessLoading] = useState(false);
  const [scriptAccessSaving, setScriptAccessSaving] = useState(false);
  const [allAtmosphereGroups, setAllAtmosphereGroups] = useState<string[]>([]);
  const [personaCategoryEnabled, setPersonaCategoryEnabled] = useState(false);
  const [feishuScriptEnabled, setFeishuScriptEnabled] = useState(true);
  const [selectedAtmosphereGroups, setSelectedAtmosphereGroups] = useState<string[]>([]);
  const [scriptAccessSummaryMap, setScriptAccessSummaryMap] = useState<Record<string, string>>({});

  const [createForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [roleForm] = Form.useForm();
  const createUsername = Form.useWatch("username", createForm);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tenantUserApi.list();
      const nextUsers = response.users || [];
      setUsers(nextUsers);
      setTotal(response.total || 0);

      const regularUsers = nextUsers.filter((user) => user.role !== "tenant_admin");
      if (regularUsers.length > 0 && dispatchAccessEnabled) {
        const counts = await Promise.all(
          regularUsers.map(async (user) => {
            try {
              const access = await trpc.userAccess.getBusinessUnits.query({ userId: user.id });
              return [user.id, access.length] as const;
            } catch {
              return [user.id, 0] as const;
            }
          })
        );
        setAccessCountMap(Object.fromEntries(counts));
      } else {
        setAccessCountMap({});
      }

      if (regularUsers.length > 0 && scriptAssistantAccessEnabled) {
        const scriptSummaries = await Promise.all(
          regularUsers.map(async (user) => {
            try {
              const access = await trpc.userAccess.getScriptAssistantAccess.query({
                userId: user.id,
              });
              const parts: string[] = [];
              if (access.feishuScriptEnabled) parts.push("飞书剧本");
              if (access.personaCategoryEnabled) parts.push("人设类目");
              if (access.atmosphereGroups.length > 0) {
                parts.push(`${access.atmosphereGroups.length}个分组`);
              }
              return [user.id, parts.length > 0 ? parts.join(" · ") : "未授权"] as const;
            } catch {
              return [user.id, "未授权"] as const;
            }
          })
        );
        setScriptAccessSummaryMap(Object.fromEntries(scriptSummaries));
      } else {
        setScriptAccessSummaryMap({});
      }
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "加载用户列表失败"));
    } finally {
      setLoading(false);
    }
  }, [dispatchAccessEnabled, message, scriptAssistantAccessEnabled]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleCreateUser = async (values: {
    username: string;
    name: string;
    password: string;
    role: "tenant_admin" | "member";
  }) => {
    try {
      await tenantUserApi.create({
        username: values.username,
        name: values.name,
        password: values.password,
        role: values.role,
      });
      message.success("用户创建成功");
      setCreateModalOpen(false);
      createForm.resetFields();
      await loadUsers();
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "创建用户失败"));
    }
  };

  const handleBanUser = async (user: TenantUserRecord) => {
    try {
      await tenantUserApi.ban({
        userId: user.id,
        reason: "管理员操作",
      });
      message.success(`已停用用户 ${user.name}`);
      await loadUsers();
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "停用用户失败"));
    }
  };

  const handleUnbanUser = async (user: TenantUserRecord) => {
    try {
      await tenantUserApi.unban({ userId: user.id });
      message.success(`已启用用户 ${user.name}`);
      await loadUsers();
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "启用用户失败"));
    }
  };

  const handleChangePassword = async (values: { newPassword: string }) => {
    if (!selectedUser) return;
    try {
      await tenantUserApi.setPassword({
        userId: selectedUser.id,
        password: values.newPassword,
      });
      message.success(`已修改用户 ${selectedUser.name} 的密码`);
      setPasswordModalOpen(false);
      passwordForm.resetFields();
      setSelectedUser(null);
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "修改密码失败"));
    }
  };

  const handleChangeRole = async (values: { role: "tenant_admin" | "member" }) => {
    if (!selectedUser) return;
    try {
      await tenantUserApi.updateRole({
        userId: selectedUser.id,
        role: values.role,
      });
      message.success(`已将用户 ${selectedUser.name} 的角色修改为 ${values.role === "tenant_admin" ? "租户管理员" : "成员"}`);
      setRoleModalOpen(false);
      roleForm.resetFields();
      setSelectedUser(null);
      await loadUsers();
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "修改角色失败"));
    }
  };

  const openAccessModal = async (user: TenantUserRecord) => {
    setSelectedUser(user);
    setAccessModalOpen(true);
    setAccessLoading(true);
    try {
      const [units, access] = await Promise.all([
        trpc.fadan.businessUnits.list.query(),
        trpc.userAccess.getBusinessUnits.query({ userId: user.id }),
      ]);
      setAllBusinessUnits(units.map((unit) => ({ id: unit.id, name: unit.name })));
      setSelectedUnitIds(access.map((item) => item.businessUnitId));
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "加载业务单元授权信息失败"));
      setAccessModalOpen(false);
      setSelectedUser(null);
    } finally {
      setAccessLoading(false);
    }
  };

  const handleSaveAccess = async () => {
    if (!selectedUser) return;
    setAccessSaving(true);
    try {
      const result = await trpc.userAccess.setBusinessUnits.mutate({
        userId: selectedUser.id,
        businessUnitIds: selectedUnitIds,
      });
      setAccessCountMap((prev) => ({ ...prev, [selectedUser.id]: result.length }));
      message.success(`已更新用户 ${selectedUser.name} 的业务单元授权`);
      setAccessModalOpen(false);
      setSelectedUser(null);
      setSelectedUnitIds([]);
      setAllBusinessUnits([]);
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "保存业务单元授权失败"));
    } finally {
      setAccessSaving(false);
    }
  };

  const openScriptAccessModal = async (user: TenantUserRecord) => {
    setSelectedUser(user);
    setScriptAccessModalOpen(true);
    setScriptAccessLoading(true);
    try {
      const [groups, access] = await Promise.all([
        trpc.atmosphereMember.groups.query(),
        trpc.userAccess.getScriptAssistantAccess.query({ userId: user.id }),
      ]);
      setAllAtmosphereGroups(groups);
      setPersonaCategoryEnabled(access.personaCategoryEnabled);
      setFeishuScriptEnabled(access.feishuScriptEnabled);
      setSelectedAtmosphereGroups(access.atmosphereGroups);
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "加载剧本助手授权信息失败"));
      setScriptAccessModalOpen(false);
      setSelectedUser(null);
    } finally {
      setScriptAccessLoading(false);
    }
  };

  const handleSaveScriptAccess = async () => {
    if (!selectedUser) return;
    setScriptAccessSaving(true);
    try {
      const result = await trpc.userAccess.setScriptAssistantAccess.mutate({
        userId: selectedUser.id,
        personaCategoryEnabled,
        feishuScriptEnabled,
        atmosphereGroups: selectedAtmosphereGroups,
      });
      const parts: string[] = [];
      if (result.feishuScriptEnabled) parts.push("飞书剧本");
      if (result.personaCategoryEnabled) parts.push("人设类目");
      if (result.atmosphereGroups.length > 0) {
        parts.push(`${result.atmosphereGroups.length}个分组`);
      }
      setScriptAccessSummaryMap((prev) => ({
        ...prev,
        [selectedUser.id]: parts.length > 0 ? parts.join(" · ") : "未授权",
      }));
      message.success(`已更新用户 ${selectedUser.name} 的剧本助手授权`);
      setScriptAccessModalOpen(false);
      setSelectedUser(null);
      setAllAtmosphereGroups([]);
      setSelectedAtmosphereGroups([]);
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "保存剧本助手授权失败"));
    } finally {
      setScriptAccessSaving(false);
    }
  };

  const columns: ColumnsType<TenantUserRecord> = [
    {
      title: "用户名",
      dataIndex: "username",
      key: "username",
      width: 140,
      fixed: "left",
      render: (username, record) => (
        <Space>
          <span style={{ fontWeight: 500 }}>{username || "-"}</span>
          {record.banned && (
            <Tooltip title={record.banReason || "已停用"}>
              <Badge status="error" />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "昵称",
      dataIndex: "name",
      key: "name",
      width: 120,
      render: (name) => <Text copyable={{ text: name }}>{name}</Text>,
    },
    {
      title: "邮箱",
      dataIndex: "email",
      key: "email",
      width: 200,
      ellipsis: true,
      render: (email) => (email ? <Text copyable={{ text: email }}>{email}</Text> : "-"),
    },
    {
      title: "角色",
      dataIndex: "role",
      key: "role",
      width: 120,
      render: (role) => roleTag(String(role || "")),
    },
    {
      title: "状态",
      dataIndex: "banned",
      key: "banned",
      width: 90,
      render: (banned) => (
        <Tag color={banned ? "error" : "success"}>{banned ? "已停用" : "正常"}</Tag>
      ),
    },
    {
      title: "已授权",
      key: "businessUnitAccess",
      width: 90,
      render: (_, record) =>
        record.role === "tenant_admin" ? (
          <Text type="secondary">全部</Text>
        ) : (
          <Tag color={accessCountMap[record.id] ? "processing" : "default"}>
            {accessCountMap[record.id] || 0} 个
          </Tag>
        ),
    },
    {
      title: "剧本助手",
      key: "scriptAssistantAccess",
      width: 180,
      ellipsis: true,
      render: (_, record) =>
        record.role === "tenant_admin" ? (
          <Text type="secondary">全部</Text>
        ) : (
          <Text type="secondary">{scriptAccessSummaryMap[record.id] || "未授权"}</Text>
        ),
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (date) => (date ? new Date(date).toLocaleString("zh-CN") : "-"),
    },
    {
      title: "操作",
      key: "actions",
      width: 240,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          {record.role !== "tenant_admin" && dispatchAccessEnabled && (
            <Tooltip title="业务单元授权">
              <Button
                type="text"
                size="small"
                icon={<TeamOutlined />}
                onClick={() => void openAccessModal(record)}
              />
            </Tooltip>
          )}
          {record.role !== "tenant_admin" && scriptAssistantAccessEnabled && (
            <Tooltip title="剧本助手授权">
              <Button
                type="text"
                size="small"
                icon={<BookOutlined />}
                onClick={() => void openScriptAccessModal(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="修改密码">
            <Button
              type="text"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => {
                setSelectedUser(record);
                setPasswordModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="修改角色">
            <Button
              type="text"
              size="small"
              icon={<UserSwitchOutlined />}
              onClick={() => {
                setSelectedUser(record);
                roleForm.setFieldsValue({
                  role: record.role === "tenant_admin" ? "tenant_admin" : "member",
                });
                setRoleModalOpen(true);
              }}
            />
          </Tooltip>
          {record.banned ? (
            <Tooltip title="启用">
              <Button
                type="text"
                size="small"
                icon={<UnlockOutlined />}
                style={{ color: "#52c41a" }}
                onClick={() => void handleUnbanUser(record)}
              />
            </Tooltip>
          ) : (
            <Popconfirm
              title="停用用户"
              description={`确定要停用用户 "${record.name}" 吗？`}
              onConfirm={() => handleBanUser(record)}
              okText="确定"
              cancelText="取消"
            >
              <Tooltip title="停用">
                <Button type="text" size="small" icon={<LockOutlined />} danger />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const visibleColumns = columns.filter((column) => {
    if (column.key === "businessUnitAccess") return dispatchAccessEnabled;
    if (column.key === "scriptAssistantAccess") return scriptAssistantAccessEnabled;
    return true;
  });

  const adminCount = users.filter((u) => u.role === "tenant_admin").length;
  const bannedCount = users.filter((u) => u.banned).length;
  const pagedUsers = users.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  return (
    <div className="fade-in">
      <div className="page-title-row" style={{ marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            用户管理
          </Title>
          <Text type="secondary">
            当前操作租户：{activeTenantLabel}
            {isPlatformSuperAdmin ? "（平台超管可管理当前 active tenant）" : ""}
          </Text>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => void loadUsers()} loading={loading}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
            创建用户
          </Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="账号由管理员创建，不支持删除用户；如需限制登录请使用停用。"
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Statistic title="用户总数" value={total} prefix={<UserOutlined />} />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic title="租户管理员" value={adminCount} prefix={<SafetyOutlined />} />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic title="已停用" value={bannedCount} prefix={<LockOutlined />} />
        </Col>
      </Row>

      <Table
        columns={visibleColumns}
        dataSource={pagedUsers}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1100 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (count) => `共 ${count} 条记录`,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize });
          },
        }}
      />

      <Modal
        title="创建用户"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
        }}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateUser}
          initialValues={{ role: "member" }}
        >
          <Form.Item
            name="username"
            label="登录账号"
            rules={[
              { required: true, message: "请输入登录账号" },
              { min: 3, message: "账号至少3个字符" },
              { pattern: /^[a-zA-Z0-9_]+$/, message: "只能包含字母、数字和下划线" },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="用于登录的账号" />
          </Form.Item>
          <Form.Item
            name="name"
            label="用户昵称"
            rules={[{ required: true, message: "请输入用户昵称" }]}
          >
            <Input placeholder="显示名称" />
          </Form.Item>
          <Form.Item label="邮箱">
            <Input
              value={createUsername ? `${createUsername}@local.dev` : ""}
              disabled
              placeholder="根据登录账号自动生成（若后端需要）"
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: "请输入密码" },
              { min: 8, message: "密码至少8位" },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入密码" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true, message: "请选择角色" }]}>
            <Select
              options={[
                { value: "member", label: "成员" },
                { value: "tenant_admin", label: "租户管理员" },
              ]}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setCreateModalOpen(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`修改密码 - ${selectedUser?.name || ""}`}
        open={passwordModalOpen}
        onCancel={() => {
          setPasswordModalOpen(false);
          passwordForm.resetFields();
          setSelectedUser(null);
        }}
        footer={null}
        destroyOnHidden
      >
        <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: "请输入新密码" },
              { min: 8, message: "密码至少8位" },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "请确认密码" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("两次输入的密码不一致"));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请再次输入密码" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setPasswordModalOpen(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                确认修改
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`修改角色 - ${selectedUser?.name || ""}`}
        open={roleModalOpen}
        onCancel={() => {
          setRoleModalOpen(false);
          roleForm.resetFields();
          setSelectedUser(null);
        }}
        footer={null}
        destroyOnHidden
      >
        <Form form={roleForm} layout="vertical" onFinish={handleChangeRole}>
          <Form.Item name="role" label="角色" rules={[{ required: true, message: "请选择角色" }]}>
            <Select
              options={[
                { value: "member", label: "成员" },
                { value: "tenant_admin", label: "租户管理员" },
              ]}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setRoleModalOpen(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                确认修改
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`业务单元授权 - ${selectedUser?.name || ""}`}
        open={accessModalOpen}
        onCancel={() => {
          setAccessModalOpen(false);
          setSelectedUser(null);
          setSelectedUnitIds([]);
          setAllBusinessUnits([]);
        }}
        onOk={() => void handleSaveAccess()}
        confirmLoading={accessSaving}
        okText="保存"
        cancelText="取消"
        destroyOnHidden
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          选择该用户可在发单监控中查看的业务单元（作用于当前租户 {context?.activeTenant.name}）。
        </Text>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="选择业务单元"
          style={{ width: "100%" }}
          loading={accessLoading}
          value={selectedUnitIds}
          options={allBusinessUnits.map((unit) => ({ label: unit.name, value: unit.id }))}
          onChange={setSelectedUnitIds}
        />
      </Modal>

      <Modal
        title={`剧本助手授权 - ${selectedUser?.name || ""}`}
        open={scriptAccessModalOpen}
        onCancel={() => {
          setScriptAccessModalOpen(false);
          setSelectedUser(null);
          setAllAtmosphereGroups([]);
          setSelectedAtmosphereGroups([]);
        }}
        onOk={() => void handleSaveScriptAccess()}
        confirmLoading={scriptAccessSaving}
        okText="保存"
        cancelText="取消"
        destroyOnHidden
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          配置该用户可访问的剧本助手功能模块。
        </Text>
        <Space orientation="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text>飞书剧本</Text>
            <Switch
              checked={feishuScriptEnabled}
              loading={scriptAccessLoading}
              onChange={setFeishuScriptEnabled}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text>人设类目</Text>
            <Switch
              checked={personaCategoryEnabled}
              loading={scriptAccessLoading}
              onChange={setPersonaCategoryEnabled}
            />
          </div>
          <div>
            <Text style={{ display: "block", marginBottom: 8 }}>气氛员分组</Text>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="选择可访问的气氛员分组"
              style={{ width: "100%" }}
              loading={scriptAccessLoading}
              value={selectedAtmosphereGroups}
              options={allAtmosphereGroups.map((group) => ({ label: group, value: group }))}
              onChange={setSelectedAtmosphereGroups}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
}
